import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  CalendarToday,
  AssignmentTurnedIn,
} from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  fetchEpics,
  createEpic,
  updateEpic,
  deleteEpic,
  clearError,
} from '../../store/epicSlice';
import { Epic, EpicStatus } from '../../types/common';
import './EpicManagement.scss';

interface EpicManagementProps {
  boardId: string;
}

const EPIC_COLORS = [
  '#FF6B35', '#4A90E2', '#50C878', '#9b59b6',
  '#f39c12', '#e74c3c', '#1abc9c', '#34495e'
];

const EpicManagement: React.FC<EpicManagementProps> = ({ boardId }) => {
  const dispatch = useAppDispatch();
  const { epics, loading, error } = useAppSelector((state) => state.epics);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingEpic, setEditingEpic] = useState<Epic | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEpic, setSelectedEpic] = useState<Epic | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: EPIC_COLORS[0],
    status: EpicStatus.PLANNING,
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    dispatch(fetchEpics(boardId));
  }, [dispatch, boardId]);

  const handleOpenDialog = (epic?: Epic) => {
    if (epic) {
      setEditingEpic(epic);
      setFormData({
        name: epic.name,
        description: epic.description || '',
        color: epic.color || EPIC_COLORS[0],
        status: epic.status,
        startDate: epic.startDate ? epic.startDate.split('T')[0] : '',
        endDate: epic.endDate ? epic.endDate.split('T')[0] : '',
      });
    } else {
      setEditingEpic(null);
      setFormData({
        name: '',
        description: '',
        color: EPIC_COLORS[0],
        status: EpicStatus.PLANNING,
        startDate: '',
        endDate: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEpic(null);
  };

  const handleSubmit = async () => {
    if (editingEpic) {
      await dispatch(
        updateEpic({
          epicId: editingEpic.id,
          data: {
            ...formData,
            startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
            endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
          },
        })
      );
    } else {
      await dispatch(
        createEpic({
          boardId,
          ...formData,
          startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
          endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        })
      );
    }
    handleCloseDialog();
  };

  const handleDeleteEpic = async (epicId: string) => {
    await dispatch(deleteEpic(epicId));
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, epic: Epic) => {
    setAnchorEl(event.currentTarget);
    setSelectedEpic(epic);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEpic(null);
  };

  const getStatusColor = (status: EpicStatus) => {
    switch (status) {
      case EpicStatus.PLANNING:
        return 'default';
      case EpicStatus.IN_PROGRESS:
        return 'primary';
      case EpicStatus.COMPLETED:
        return 'success';
      case EpicStatus.ON_HOLD:
        return 'warning';
      case EpicStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateProgress = (epic: any) => {
    if (epic.progressPercentage !== undefined && epic.progressPercentage !== null) {
      return Number(epic.progressPercentage);
    }
    if (!epic.totalStoryPoints || epic.totalStoryPoints === 0) return 0;
    return Math.round(((epic.completedStoryPoints || 0) / epic.totalStoryPoints) * 100);
  };

  return (
    <Box className="epic-management">
      <Box className="epic-management__header">
        <Typography variant="h5" className="epic-management__title">
          Epics
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          className="epic-management__add-btn"
        >
          Create Epic
        </Button>
      </Box>

      {/* Epic List */}
      <Box className="epic-list" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
        {epics.map((epic) => (
          <Box key={epic.id}>
            <Card className="epic-card" sx={{ borderTop: `4px solid ${epic.color}`, height: '100%' }}>
              <CardContent>
                <Box className="epic-card__header">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" className="epic-card__name">
                      {epic.name}
                    </Typography>
                    <Chip
                      label={(epic.status || EpicStatus.PLANNING).replace('_', ' ')}
                      color={getStatusColor(epic.status || EpicStatus.PLANNING)}
                      size="small"
                      className="epic-card__status"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, epic)}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                </Box>

                {epic.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="epic-card__description"
                    sx={{ mt: 2, mb: 2 }}
                  >
                    {epic.description}
                  </Typography>
                )}

                {(epic.startDate || epic.endDate) && (
                  <Box className="epic-card__dates" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {epic.startDate && new Date(epic.startDate).toLocaleDateString()}
                      {epic.startDate && epic.endDate && ' - '}
                      {epic.endDate && new Date(epic.endDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                <Box className="epic-card__progress" sx={{ mt: 2 }}>
                  <Box className="epic-card__progress-header" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Progress</Typography>
                    <Typography variant="body2">
                      {epic.completedTasks !== undefined ? `${epic.completedTasks} / ${epic.totalTasks || 0} Tasks` : `${epic.completedStoryPoints || 0} / ${epic.totalStoryPoints || 0} SP`} ({Math.round(calculateProgress(epic))}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={calculateProgress(epic)}
                    className="epic-card__progress-bar"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Empty State */}
      {epics.length === 0 && !loading && (
        <Box className="epic-management__empty" sx={{ textAlign: 'center', py: 8 }}>
          <AssignmentTurnedIn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Epics Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first epic to organize related tasks
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Create Epic
          </Button>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEpic ? 'Edit Epic' : 'Create New Epic'}
        </DialogTitle>
        <DialogContent>
          <Box className="epic-dialog" sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Epic Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as EpicStatus })}
              >
                {Object.values(EpicStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace('_', ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Color</InputLabel>
              <Select
                value={formData.color}
                label="Color"
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              >
                {EPIC_COLORS.map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: color,
                          borderRadius: 1,
                        }}
                      />
                      {color}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            {editingEpic ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(selectedEpic!);
          handleMenuClose();
        }}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteEpic(selectedEpic!.id)}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Error Alert Snackbar */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => dispatch(clearError())}
      >
        <Alert
          onClose={() => dispatch(clearError())}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EpicManagement;
